import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { ACTIVE_TAB } from "@/app/constants/global-constants";
import {
  isInternalBrowserQueryKey,
  sanitizeBrowserQueryParams,
} from "@/utils/form/queryParams";

export type MutateParamsOptions = {
  replace?: boolean;
};

export type useQueryFilterType = {
  keys: Record<string, string | null | undefined>;
  updateParams: (key: string, value: string) => void;
  clearParams: () => void;
  clearParamsPreservingKeys: (preserveKeys: readonly string[]) => void;
  searchParams: URLSearchParams;
  clearParam: (key: string) => void;
  getAllValue: (key: string) => string[] | null;
  appendSameKeys: (key: string, value: string) => void;
  replaceSameKeys: (key: string, values: readonly string[]) => void;
  mutateParams: (
    mutator: (params: URLSearchParams) => void,
    options?: MutateParamsOptions,
  ) => void;
  getActiveTab: <T extends string>() => T | null;
};

/**
 * Provides helpers for reading and writing URL query filters.
 *
 * @returns {useQueryFilterType} Query filter state and mutation helpers.
 */
export const useQueryParams = (): useQueryFilterType => {
  const [params, setParams] = useSearchParams();
  const visibleParams = useMemo(
    () => sanitizeBrowserQueryParams(params),
    [params],
  );

  const clearParam = useCallback(
    (key: string) => {
      if (isInternalBrowserQueryKey(key)) {
        return;
      }
      setParams((currentParams) => {
        const nextParams = sanitizeBrowserQueryParams(currentParams);
        nextParams.delete(key);

        return nextParams;
      });
    },
    [setParams],
  );

  const updateParams = useCallback(
    (key: string, value: string) => {
      if (isInternalBrowserQueryKey(key)) {
        return;
      }

      setParams((currentParams) => {
        const nextParams = sanitizeBrowserQueryParams(currentParams);

        if (!value) {
          nextParams.delete(key);
        } else {
          nextParams.set(key, value);
        }
        return nextParams;
      });
    },
    [setParams],
  );

  const replaceSameKeys = useCallback(
    (key: string, values: readonly string[]) => {
      if (isInternalBrowserQueryKey(key)) {
        return;
      }

      setParams((currentParams) => {
        const nextParams = sanitizeBrowserQueryParams(currentParams);
        nextParams.delete(key);
        values.forEach((value) => {
          if (value) {
            nextParams.append(key, value);
          }
        });

        return sanitizeBrowserQueryParams(nextParams);
      });
    },
    [setParams],
  );

  const appendSameKeys = useCallback(
    (key: string, value: string) => {
      if (isInternalBrowserQueryKey(key) || !value) {
        return;
      }

      setParams((currentParams) => {
        const nextParams = sanitizeBrowserQueryParams(currentParams);
        nextParams.append(key, value);

        return sanitizeBrowserQueryParams(nextParams);
      });
    },
    [setParams],
  );

  const clearParams = useCallback(() => {
    setParams((currentParams) => {
      const nextParams = new URLSearchParams();
      // Preserve `ig_*` keys ("ig" stands for ignore key). These mirror UI
      // state like the active tab into the URL and are not user-applied
      // filters, so clearing filters must not strip them.
      Array.from(currentParams.keys()).forEach((key) => {
        if (key.startsWith("ig_")) {
          const values = currentParams.getAll(key);
          values.forEach((value) => nextParams.append(key, value));
        }
      });

      return nextParams;
    });
  }, [setParams]);

  const clearParamsPreservingKeys = useCallback(
    (preserveKeys: readonly string[]) => {
      const preserve = new Set(preserveKeys);
      setParams((currentParams) => {
        const nextParams = sanitizeBrowserQueryParams(currentParams);

        Array.from(nextParams.keys()).forEach((key) => {
          // `ig_*` keys ("ig" stands for ignore key) mirror UI state into
          // the URL and are not filters; always preserve them alongside
          // any caller-provided keys.
          if (preserve.has(key) || key.startsWith("ig_")) {
            return;
          }
          nextParams.delete(key);
        });

        return nextParams;
      });
    },
    [setParams],
  );

  const getAllValue = useCallback(
    (key: string) => {
      return visibleParams.getAll(key);
    },
    [visibleParams],
  );

  const mutateParams = useCallback(
    (
      mutator: (params: URLSearchParams) => void,
      options?: MutateParamsOptions,
    ) => {
      setParams(
        (currentParams) => {
          const nextParams = sanitizeBrowserQueryParams(currentParams);
          mutator(nextParams);

          return sanitizeBrowserQueryParams(nextParams);
        },
        { replace: options?.replace ?? false },
      );
    },
    [setParams],
  );

  const keys = useMemo(() => {
    const result: Record<string, string | null | undefined> = {};
    visibleParams.forEach((value, key) => {
      result[key] = value ?? null;
    });

    return result;
  }, [visibleParams]);

  const getActiveTab = useCallback(<T extends string>() => {
    return visibleParams.get(ACTIVE_TAB) as T | null;
  }, [visibleParams]);

  return {
    keys,
    clearParam,
    clearParams,
    clearParamsPreservingKeys,
    searchParams: visibleParams,
    updateParams,
    getAllValue,
    appendSameKeys,
    replaceSameKeys,
    mutateParams,
    getActiveTab,
  };
};

const useQueryParamsDefault = useQueryParams;
export default useQueryParamsDefault;
